// If a page crashes while drawing, show a way out instead of a blank screen, and report it.
// A page that loads separately (React.lazy) fails with a ChunkLoadError when a deploy replaced its
// file while the app was open: that one reloads the page once to get the new files instead.
import { Component } from 'react';
import { reportError } from './errorReport';
import { useI18n } from './i18n';

function Fallback() {
  const { t } = useI18n();
  return (
    <div className="App error-screen">
      <h1>{t('Something went wrong on this page')}</h1>
      <p>{t("We've been told about it. Reloading usually fixes it, and your journal is safe.")}</p>
      <div className="error-screen-actions">
        <button type="button" onClick={() => window.location.reload()}>{t('Reload the page')}</button>
        <a href="/app/home" className="secondary-link">{t('Go to Home')}</a>
      </div>
    </div>
  );
}

const RELOADED = 'chunkReloadAt';
export const isChunkError = (error) => !!error && (error.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(error.message || ''));

// Reload at most once a minute, so a file that's really missing can't loop.
function reloadForNewVersion() {
  try {
    const last = Number(sessionStorage.getItem(RELOADED) || 0);
    if (Date.now() - last < 60000) return false;
    sessionStorage.setItem(RELOADED, String(Date.now()));
  } catch (e) {
    return false;
  }
  window.location.reload();
  return true;
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    if (isChunkError(error) && reloadForNewVersion()) return;
    const where = info && info.componentStack ? info.componentStack.trim().split('\n')[0].trim() : 'screen';
    reportError(error, `page crashed (${where})`);
  }

  render() {
    return this.state.failed ? <Fallback /> : this.props.children;
  }
}
